import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "../../utils/toast";
import { SearchableSelect } from "../ui";
import CallFields from "../calls/CallFields";
import CreateDraftDialog from "../calls/CreateDraftDialog";
import { useDraftForm } from "../calls/forms";
import { errorMessageOf, fieldErrorsOf, newIdempotencyKey, problemOf, scholarshipsApi } from "../../api/scholarships";
import {
  KINDS,
  SCHOLARSHIPS_KEY,
  needsSponsor,
  closesAtOf,
  countryOptionsOf,
  opensAtOf,
  sponsorOptionsOf,
  staffScholarshipKey,
  todayInKigali,
  useOptions,
  useSponsors,
} from "./model";

const EMPTY_FORM = {
  name: "",
  sponsorId: "",
  academicYearId: "",
  destinationCountryCode: "",
  opensDate: "",
  closesDate: "",
  awardQuota: "",
  visibility: "PUBLIC",
  publicSummary: "",
  description: "",
};

// The API names the dates opensAt/closesAt; the form edits them as Kigali calendar days
const FORM_FIELD = { opensAt: "opensDate", closesAt: "closesDate" };

const PLACEHOLDERS = {
  COOPERATION: "e.g. Chinese Government Scholarship 2026/27",
  GOVERNMENT: "e.g. Abroad loan 2026/27: Masters in China",
};

// An abroad loan student names their own country
const asksDestination = (kind) => !kind.studentChoosesPlace;

// A draft needs only its name; the rest can follow before publishing
const validate = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (form.opensDate && form.closesDate && form.closesDate < form.opensDate) errors.closesDate = "Closing date must be on or after the opening date";
  if (form.awardQuota !== "" && !(Number.isInteger(Number(form.awardQuota)) && Number(form.awardQuota) >= 1)) errors.awardQuota = "Enter a whole number of 1 or more";
  return errors;
};

// The kind decides the type: a scholarship or a loan call, never changed afterwards. Empty fields are left out
const toBody = (form, kind) => ({
  name: form.name.trim(),
  type: kind.type,
  ...(needsSponsor(kind.type) && form.sponsorId && { sponsorId: form.sponsorId }),
  ...(form.academicYearId && { academicYearId: form.academicYearId }),
  ...(!kind.studentChoosesPlace && form.destinationCountryCode && { destinationCountryCode: form.destinationCountryCode }),
  ...(form.opensDate && { opensAt: opensAtOf(form.opensDate) }),
  ...(form.closesDate && { closesAt: closesAtOf(form.closesDate) }),
  awardQuota: form.awardQuota === "" ? null : Number(form.awardQuota),
  visibility: form.visibility,
  ...(form.publicSummary.trim() && { publicSummary: form.publicSummary.trim() }),
  ...(form.description.trim() && { description: form.description.trim() }),
});

/**
 * Creates a draft scholarship (journey step 1), then hands it to `onCreated` so its set-up can
 * continue. Closing the dialog keeps the draft; Cancel or a successful create clears it.
 */
export default function CreateScholarshipDialog({ kind = KINDS.scholarship, open, onClose, onCreated }) {
  const queryClient = useQueryClient();
  const { form, setForm, errors, setErrors, reset } = useDraftForm(`scholarship-new:${kind.type}`, EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  // One Idempotency-Key per create; a retry of the same form reuses it, any change starts a new one
  const keyRef = useRef(null);
  useEffect(() => {
    keyRef.current = null;
  }, [form]);

  // Settings decide the lists: academic years from the current one, and the destinations offered
  const { data: options } = useOptions();
  const { data: sponsors = [], isError: sponsorsFailed } = useSponsors();
  const sponsorOptions = useMemo(() => sponsorOptionsOf(sponsors), [sponsors]);
  const countryOptions = useMemo(() => countryOptionsOf(options?.destinationCountries), [options]);

  // Choosing a sponsor fills in its country as the destination, when none is chosen yet and it is offered
  const update = (e) => {
    const { name, value } = e.target;
    const sponsorCountry = name === "sponsorId" && sponsors.find((s) => s.id === value)?.countryCode;
    const prefill = sponsorCountry && countryOptions.some((o) => o.value === sponsorCountry);
    setForm((prev) => ({ ...prev, [name]: value, ...(prefill && !prev.destinationCountryCode && { destinationCountryCode: sponsorCountry }) }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (prefill && errors.destinationCountryCode) setErrors((prev) => ({ ...prev, destinationCountryCode: undefined }));
  };

  const close = () => {
    setErrors({});
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    keyRef.current ??= newIdempotencyKey();
    setSubmitting(true);
    try {
      const { data, etag } = await scholarshipsApi.post("admin/scholarships", toBody(form, kind), { idempotencyKey: keyRef.current });
      queryClient.setQueryData(staffScholarshipKey(data.id), { data, etag });
      queryClient.invalidateQueries({ queryKey: SCHOLARSHIPS_KEY });
      toast.success(`Draft ${kind.one} created. Continue its set-up.`);
      reset();
      onCreated(data);
    } catch (error) {
      console.error(`Failed to create ${kind.one}:`, error);
      const serverErrors = fieldErrorsOf(problemOf(error));
      setErrors(Object.fromEntries(Object.entries(serverErrors).map(([field, message]) => [FORM_FIELD[field] ?? field, message])));
      toast.error(errorMessageOf(error, `Failed to create the ${kind.one}. Please try again.`));
    } finally {
      setSubmitting(false);
    }
  };

  const field = (name) => ({ name, value: form[name], onChange: update, error: errors[name] });

  return (
    <CreateDraftDialog
      open={open}
      title={`New ${kind.one}`}
      formId="create-scholarship"
      submitting={submitting}
      onClose={close}
      onCancel={() => {
        reset();
        onClose();
      }}
      onSubmit={submit}
    >
      <CallFields
        draft
        field={field}
        namePlaceholder={PLACEHOLDERS[kind.type]}
        quotaLabel="Number of awards"
        summaryMax={2000}
        descriptionMax={10000}
        opensMin={todayInKigali()}
        closesMin={form.opensDate || todayInKigali()}
      >
        {needsSponsor(kind.type) && (
          <SearchableSelect
            label="Partner"
            placeholder={sponsorsFailed ? "Could not load partners" : "Type or select the partner"}
            options={sponsorOptions}
            {...field("sponsorId")}
          />
        )}
        {asksDestination(kind) && <SearchableSelect label="Destination country" placeholder="Type or select a country" options={countryOptions} {...field("destinationCountryCode")} />}
      </CallFields>
    </CreateDraftDialog>
  );
}
